import { computed, ref } from "vue";

export type AppLocale = "de" | "en";

const STORAGE_KEY = "moonshot:locale";
const initialLocale = (() => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "de" || stored === "en") return stored;
  } catch { /* storage may be unavailable */ }
  return "de";
})();

export const locale = ref<AppLocale>(initialLocale);

const messages = {
  de: {
    "nav.visitors": "Besucher",
    "nav.statistics": "Statistiken",
    "nav.quality": "Qualität",
    "nav.settings": "Einstellungen",
    "nav.main": "Hauptnavigation",
    "header.switchSite": "Website wechseln",
    "header.notifications": "Benachrichtigungen",
    "header.userMenu": "Benutzermenü",
    "header.activeSite": "Aktive Site",
    "header.active": "Aktiv",
    "header.addSite": "Website hinzufügen",
    "header.manageSites": "Verwalten",
    "header.siteOverview": "Websites",
    "header.siteOverviewHelp": "Zwischen Websites wechseln",
    "header.siteConnected": "Verbunden",
    "header.siteDegraded": "Erkennung eingeschränkt",
    "header.siteWaiting": "Noch kein Signal",
    "header.siteStale": "Verbindung prüfen",
    "header.siteError": "Störung",
    "header.sitePaused": "Pausiert",
    "header.privacyPending": "Datenschutz offen",
    "header.setupPending": "Einrichtung offen",
    "header.qualityComparison": "Qualitätsvergleich",
    "header.siteActive": "Aktiv",
    "header.siteCheck": "Prüfen",
    "header.noSites": "Noch keine Website eingerichtet.",
    "header.organization": "Organisation",
    "header.readOnly": "Nur lesen",
    "header.admin": "Admin",
    "header.noCritical": "Alles in Ordnung",
    "header.workspaceReachable": "Workspace und Live-Kanal sind erreichbar.",
    "header.demo": "Lokaler Demo-Modus",
    "header.rolesActive": "Angemeldet",
    "header.logout": "Abmelden",
    "header.connectFirebase": "Firebase verbinden",
    "header.language": "Sprache",
    "system.demo": "Demo-Daten",
    "system.loading": "Daten werden geladen",
    "system.check": "System prüfen",
    "system.accountInactive": "Account Mode inaktiv",
    "system.graphMissing": "Resolver ohne Graph",
    "system.connectorStale": "Connector aktualisieren",
    "system.noTraffic": "Kein Edge-Traffic",
    "system.limited": "CDN-Erfassung prüfen",
    "system.connecting": "Live-Verbindung",
    "system.live": "System live",
    "system.current": "Daten aktuell",
    "workspace.unavailable": "Produktionsdaten nicht verfügbar:",
    "common.retry": "Erneut versuchen",
    "common.copy": "Kopieren",
    "common.copied": "Kopiert",
    "common.next": "Weiter",
    "common.back": "Zurück",
    "common.close": "Schließen",
    "common.loading": "Wird geladen …",
    "auth.loading": "Moonshot wird geladen …",
    "auth.loginTitle": "Bei Moonshot anmelden",
    "auth.loginDescription": "Sicherer Zugriff auf deinen Organisations-Workspace.",
    "auth.email": "E-Mail",
    "auth.password": "Passwort",
    "auth.name": "Name",
    "auth.login": "Anmelden",
    "auth.register": "Organisation erstellen",
    "auth.registerLink": "Neue Organisation anlegen",
    "auth.loginLink": "Bereits registriert? Anmelden",
    "auth.loginFailed": "Anmeldung fehlgeschlagen. Bitte Zugang prüfen.",
    "auth.invalidCredentials": "E-Mail-Adresse oder Passwort stimmen nicht. Wenn du über eine Einladung kommst und noch keinen Account hast, wähle „Neuen Account erstellen“.",
    "auth.accountExists": "Für diese E-Mail-Adresse gibt es bereits einen Account. Melde dich mit deinem vorhandenen Passwort an; die Einladung wird danach automatisch angenommen.",
    "auth.weakPassword": "Das Passwort ist zu schwach. Verwende mindestens acht Zeichen.",
    "auth.invalidEmail": "Bitte prüfe die E-Mail-Adresse.",
    "auth.networkError": "Die Anmeldung konnte den Server nicht erreichen. Bitte prüfe die Verbindung und versuche es erneut.",
    "auth.tooManyRequests": "Zu viele Anmeldeversuche. Bitte warte kurz und versuche es erneut.",
    "auth.invitationFailed": "Die Anmeldung war erfolgreich, aber der Beitritt zur Organisation konnte nicht abgeschlossen werden. Bitte versuche die Einladung erneut.",
    "auth.invitationEmailMismatch": "Diese Einladung gehört zu einer anderen E-Mail-Adresse. Verwende exakt die im Link angegebene Adresse.",
    "auth.accountAssigned": "Der Organisationsbeitritt konnte nicht abgeschlossen werden. Bitte lade die Einladung neu und versuche es erneut.",
    "auth.invitationChecksMissing": "Bitte bestätige die Nutzungsbedingungen und die Verantwortung für die korrekte Verwendung.",
    "auth.siteIdExists": "Diese interne Site-ID ist bereits vergeben. Gehe einen Schritt zurück und ändere den Organisations- oder Site-Namen.",
    "auth.registrationFailed": "Die Organisation konnte nicht erstellt werden.",
    "auth.organization": "Organisation",
    "auth.legalName": "Rechtlicher Firmenname",
    "auth.address": "Vollständige Geschäftsanschrift",
    "auth.privacyEmail": "Datenschutzkontakt",
    "auth.siteName": "Name der ersten Site",
    "auth.siteId": "Site-ID",
    "auth.domain": "Primäre Domain",
    "auth.stack": "Technologie",
    "auth.terms": "Ich akzeptiere die Moonshot-Nutzungsbedingungen und bestätige, für diesen Account vertretungsberechtigt zu sein.",
    "auth.dpa": "Ich schließe die Auftragsverarbeitungsvereinbarung für die Organisation ab.",
    "auth.responsibility": "Ich übernehme die Verantwortung für die korrekte Einbindung, Rechtsgrundlage, Einwilligungskonfiguration und zulässige Nutzung auf unseren Websites.",
    "auth.legalDraft": "Nutzungsbedingungen & Auftragsverarbeitung",
    "auth.acceptContinue": "Akzeptieren und fortfahren",
    "auth.governanceTitle": "Aktualisierte Vertragsgrundlagen",
    "auth.governanceDescription": "Einmalige Bestätigung für diesen Account. Danach sind keine siteweisen Rechts- oder Resolver-Freigaben mehr nötig.",
    "auth.joinTitle": "Organisation beitreten",
    "auth.join": "Beitreten",
    "auth.inviteDescription": "Nimm die Einladung mit der eingeladenen E-Mail-Adresse an.",
    "auth.createAccount": "Account erstellen",
    "auth.existingAccount": "Bereits einen Account?",
    "auth.newAccount": "Neuen Account erstellen",
    "auth.signedInAs": "Du bist aktuell angemeldet als",
    "auth.wrongAccountTitle": "Andere E-Mail-Adresse angemeldet",
    "auth.wrongAccountDescription": "Die Einladung ist an eine andere Adresse gebunden. Melde den aktuellen Account ab und fahre anschließend mit der eingeladenen Adresse fort.",
    "auth.signOutContinue": "Abmelden und fortfahren",
    "auth.confirmJoinTitle": "Einladung bestätigen",
    "auth.confirmJoinDescription": "Dein Account ist erkannt. Bestätige noch die Bedingungen, dann erhältst du Zugriff auf die Organisation.",
    "auth.joinAs": "Beitreten als",
    "auth.stepAccount": "Zugang",
    "auth.stepOrganization": "Unternehmen",
    "auth.stepWebsite": "Website",
    "auth.stepConfirm": "Bestätigen",
    "auth.accountStepTitle": "Deinen Zugang anlegen",
    "auth.accountStepDescription": "Mit diesen Daten meldest du dich später bei Moonshot an.",
    "auth.organizationStepTitle": "Unternehmen hinterlegen",
    "auth.organizationStepDescription": "Diese Angaben werden für den Workspace und die Datenschutzvorlage verwendet.",
    "auth.websiteStepTitle": "Erste Website vorbereiten",
    "auth.websiteStepDescription": "Gib die öffentliche Domain an. Danach erhältst du denselben One-Tag und denselben Live-Test wie jede andere Website.",
    "auth.confirmStepTitle": "Angaben prüfen und bestätigen",
    "auth.confirmStepDescription": "Moonshot richtet danach den Workspace und die erste Website ein.",
    "auth.domainHelp": "Nur die Domain ohne Pfad, zum Beispiel example.com.",
    "auth.secureWorkspace": "Sicherer Workspace",
    "auth.invitationInvalid": "Die Einladung ist ungültig oder abgelaufen.",
    "auth.acceptanceFailed": "Die Bestätigung konnte nicht gespeichert werden.",
    "auth.statusTitle": "Sicherheitsprüfung nicht abgeschlossen",
    "auth.statusFailed": "Der Organisations- und Vertragsstatus konnte nicht geladen werden. Der Workspace bleibt bis zur erfolgreichen Prüfung gesperrt.",
    "setup.title": "Deine erste Website einrichten",
    "setup.description": "Wir führen dich durch Standardinstallation, Datenschutztext und einen abschließenden Funktionstest.",
    "setup.browser": "Besucher mit Zustimmung",
    "setup.connector": "Firmenbesuche ohne Zustimmung",
    "setup.privacy": "Datenschutztext",
    "setup.openWorkspace": "Workspace öffnen",
    "setup.secretWarning": "Diese Server-Verbindung ermöglicht die Firmenerkennung auch dann, wenn ein Besucher das Banner nicht akzeptiert. Übergib den Code an die Person, die deine Website oder deinen Server betreut. Das Secret darf nie in sichtbaren Browser-Code gelangen.",
    "setup.browserDescription": "Verbinde das Browser-SDK mit dem bereits vorhandenen Cookie- oder Consent-Tool deiner Website.",
    "setup.or": "oder",
    "setup.edgeEnvironment": "Server-Konfiguration",
    "setup.platformPolicyTitle": "Firmenidentifizierung freigeschaltet",
    "setup.platformPolicyDescription": "Die erforderlichen Resolver- und Firmendatenquellen werden zentral verwaltet. Für diese Website ist keine weitere Freigabe nötig.",
    "setup.active": "Aktiv",
    "setup.privacyDescription": "Übernimm die Vorlage in die Datenschutzerklärung der Site und lasse die endgültige websitespezifische Fassung vor dem Produktivbetrieb rechtlich prüfen.",
    "setup.check": "Prüfung",
    "setup.checkTitle": "Installationsprüfung",
    "setup.checkDescription": "Der Test prüft getrennt, ob Einwilligung, Besuchertracking und Firmenidentifizierung korrekt ankommen. Dabei wird keine dauerhafte Besucheridentität angelegt.",
    "setup.startCheck": "Prüfung starten",
    "setup.checkStarted": "Prüfung gestartet",
    "setup.checkExpiry": "Der Token läuft ab am",
    "setup.testSnippet": "Einmaliger Test im Browser",
    "setup.refreshStatus": "Status aktualisieren",
    "setup.browserCheck": "Browser",
    "setup.consentCheck": "Consent",
    "setup.edgeCheck": "Edge",
    "setup.pending": "ausstehend",
    "setup.checkFailed": "Die Installationsprüfung ist fehlgeschlagen.",
    "activities.label": "Aktivitäten",
    "activities.loading": "Aktivitäten werden geladen …",
    "activities.loadingMore": "Weitere Aktivitäten werden geladen …",
    "activities.empty": "Keine passenden Aktivitäten.",
    "visit.first": "Erstbesuch",
    "visit.returning": "Wiederkehrend",
    "visit.noun": "Besuch",
    "visit.recognized": "Erkannter Besucher",
    "visit.visitor": "Besucher",
    "visit.technical": "Technischer Hosting-/VPN-Abruf",
    "visit.deviceMissing": "Gerät nicht erhoben",
    "visit.select": "auswählen",
    "consent.accepted": "Akzeptiert",
    "consent.notGranted": "Keine Einwilligung",
    "consent.withdrawn": "Widerrufen",
    "consent.expired": "Abgelaufen",
    "consent.unknown": "Nicht erfasst",
    "map.label": "Aktivitätskarte",
    "map.precise": "Kartenpunkte",
    "map.loading": "Kartendaten werden geladen …",
    "map.recognized": "Erkannte Firma",
    "map.noConsent": "Keine Einwilligung",
    "map.accepted": "Akzeptiert",
    "map.live": "Live",
    "map.countryActivity": "Länderebene (kein genauer Ort)",
    "map.visitors": "Besucher",
    "map.recognizedVisitors": "Erkannte Firmen",
    "map.bots": "Bots & Technik",
    "map.recentered": "Auf sichtbare Aktivitäten ausgerichtet",
    "map.2dActive": "2D-Ansicht aktiv",
    "map.3dActive": "3D-Ansicht aktiv"
  },
  en: {
    "nav.visitors": "Visitors",
    "nav.statistics": "Statistics",
    "nav.quality": "Quality",
    "nav.settings": "Settings",
    "nav.main": "Main navigation",
    "header.switchSite": "Switch website",
    "header.notifications": "Notifications",
    "header.userMenu": "User menu",
    "header.activeSite": "Active site",
    "header.active": "Active",
    "header.addSite": "Add website",
    "header.manageSites": "Manage",
    "header.siteOverview": "Websites",
    "header.siteOverviewHelp": "Switch between websites",
    "header.siteConnected": "Connected",
    "header.siteDegraded": "Recognition limited",
    "header.siteWaiting": "No signal yet",
    "header.siteStale": "Check connection",
    "header.siteError": "Issue",
    "header.sitePaused": "Paused",
    "header.privacyPending": "Privacy pending",
    "header.setupPending": "Setup pending",
    "header.qualityComparison": "Quality comparison",
    "header.siteActive": "Active",
    "header.siteCheck": "Check",
    "header.noSites": "No website has been configured yet.",
    "header.organization": "Organization",
    "header.readOnly": "Read only",
    "header.admin": "Admin",
    "header.noCritical": "All good",
    "header.workspaceReachable": "Workspace and live channel are available.",
    "header.demo": "Local demo mode",
    "header.rolesActive": "Signed in",
    "header.logout": "Sign out",
    "header.connectFirebase": "Connect Firebase",
    "header.language": "Language",
    "system.demo": "Demo data",
    "system.loading": "Loading data",
    "system.check": "Check system",
    "system.accountInactive": "Account Mode inactive",
    "system.graphMissing": "Resolver without graph",
    "system.connectorStale": "Update connector",
    "system.noTraffic": "No edge traffic",
    "system.limited": "Check CDN capture",
    "system.connecting": "Live connection",
    "system.live": "System live",
    "system.current": "Data current",
    "workspace.unavailable": "Production data unavailable:",
    "common.retry": "Try again",
    "common.copy": "Copy",
    "common.copied": "Copied",
    "common.next": "Next",
    "common.back": "Back",
    "common.close": "Close",
    "common.loading": "Loading …",
    "auth.loading": "Loading Moonshot …",
    "auth.loginTitle": "Sign in to Moonshot",
    "auth.loginDescription": "Secure access to your organization workspace.",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.name": "Name",
    "auth.login": "Sign in",
    "auth.register": "Create organization",
    "auth.registerLink": "Create a new organization",
    "auth.loginLink": "Already registered? Sign in",
    "auth.loginFailed": "Sign-in failed. Please check your credentials.",
    "auth.invalidCredentials": "The email address or password is incorrect. If you followed an invitation and do not have an account yet, choose “Create a new account”.",
    "auth.accountExists": "An account already exists for this email address. Sign in with your existing password and the invitation will be accepted automatically.",
    "auth.weakPassword": "The password is too weak. Use at least eight characters.",
    "auth.invalidEmail": "Please check the email address.",
    "auth.networkError": "Sign-in could not reach the server. Check your connection and try again.",
    "auth.tooManyRequests": "Too many sign-in attempts. Wait briefly and try again.",
    "auth.invitationFailed": "Sign-in succeeded, but joining the organization could not be completed. Please retry the invitation.",
    "auth.invitationEmailMismatch": "This invitation belongs to a different email address. Use exactly the address shown in the invitation.",
    "auth.accountAssigned": "Joining the organization could not be completed. Reload the invitation and try again.",
    "auth.invitationChecksMissing": "Please accept the terms and responsibility for correct use.",
    "auth.siteIdExists": "This internal site ID is already in use. Go back one step and change the organization or site name.",
    "auth.registrationFailed": "The organization could not be created.",
    "auth.organization": "Organization",
    "auth.legalName": "Legal company name",
    "auth.address": "Full business address",
    "auth.privacyEmail": "Privacy contact",
    "auth.siteName": "First site name",
    "auth.siteId": "Site ID",
    "auth.domain": "Primary domain",
    "auth.stack": "Technology",
    "auth.terms": "I accept the Moonshot terms and confirm that I am authorized to represent this account.",
    "auth.dpa": "I enter into the data processing agreement for the organization.",
    "auth.responsibility": "I accept responsibility for correct installation, legal basis, consent configuration and permitted use on our websites.",
    "auth.legalDraft": "Terms of use & data processing",
    "auth.acceptContinue": "Accept and continue",
    "auth.governanceTitle": "Updated contractual basis",
    "auth.governanceDescription": "One-time confirmation for this account. No site-level legal or resolver approvals are required afterwards.",
    "auth.joinTitle": "Join organization",
    "auth.join": "Join",
    "auth.inviteDescription": "Accept the invitation with the invited email address.",
    "auth.createAccount": "Create account",
    "auth.existingAccount": "Already have an account?",
    "auth.newAccount": "Create a new account",
    "auth.signedInAs": "You are currently signed in as",
    "auth.wrongAccountTitle": "A different email address is signed in",
    "auth.wrongAccountDescription": "The invitation is tied to another address. Sign out the current account and then continue with the invited address.",
    "auth.signOutContinue": "Sign out and continue",
    "auth.confirmJoinTitle": "Confirm invitation",
    "auth.confirmJoinDescription": "Your account was recognized. Confirm the terms to receive access to the organization.",
    "auth.joinAs": "Join as",
    "auth.stepAccount": "Access",
    "auth.stepOrganization": "Company",
    "auth.stepWebsite": "Website",
    "auth.stepConfirm": "Confirm",
    "auth.accountStepTitle": "Create your access",
    "auth.accountStepDescription": "Use these details to sign in to Moonshot later.",
    "auth.organizationStepTitle": "Add your company",
    "auth.organizationStepDescription": "These details are used for the workspace and privacy notice template.",
    "auth.websiteStepTitle": "Prepare the first website",
    "auth.websiteStepDescription": "Enter the public domain. You will then receive the same One-Tag and live test as every other website.",
    "auth.confirmStepTitle": "Review and confirm",
    "auth.confirmStepDescription": "Moonshot will then prepare the workspace and first website.",
    "auth.domainHelp": "Enter only the domain without a path, for example example.com.",
    "auth.secureWorkspace": "Secure workspace",
    "auth.invitationInvalid": "The invitation is invalid or has expired.",
    "auth.acceptanceFailed": "The acceptance could not be saved.",
    "auth.statusTitle": "Security check incomplete",
    "auth.statusFailed": "The organization and governance status could not be loaded. The workspace remains locked until the check succeeds.",
    "setup.title": "Set up your first website",
    "setup.description": "We guide you through the standard installation, privacy notice and a final functionality check.",
    "setup.browser": "Visitors with consent",
    "setup.connector": "Company visits without consent",
    "setup.privacy": "Privacy notice",
    "setup.openWorkspace": "Open workspace",
    "setup.secretWarning": "This server connection enables company identification even when a visitor does not accept the banner. Give the code to the person operating your website or server. Never expose the secret in browser code.",
    "setup.browserDescription": "Connect the browser SDK to your website's existing cookie or consent tool.",
    "setup.or": "or",
    "setup.edgeEnvironment": "Server configuration",
    "setup.platformPolicyTitle": "Company identification enabled",
    "setup.platformPolicyDescription": "The required resolver and company data sources are centrally managed. No additional approval is needed for this website.",
    "setup.active": "Active",
    "setup.privacyDescription": "Copy the template into the site's privacy notice and have the final website-specific version legally reviewed before production use.",
    "setup.check": "Check",
    "setup.checkTitle": "Installation check",
    "setup.checkDescription": "The test separately checks whether consent, visitor tracking and company identification arrive correctly. It does not create a persistent visitor identity.",
    "setup.startCheck": "Start check",
    "setup.checkStarted": "Check started",
    "setup.checkExpiry": "The token expires at",
    "setup.testSnippet": "One-time browser test",
    "setup.refreshStatus": "Refresh status",
    "setup.browserCheck": "Browser",
    "setup.consentCheck": "Consent",
    "setup.edgeCheck": "Edge",
    "setup.pending": "pending",
    "setup.checkFailed": "The installation check failed.",
    "activities.label": "Activities",
    "activities.loading": "Loading activities …",
    "activities.loadingMore": "Loading more activities …",
    "activities.empty": "No matching activities.",
    "visit.first": "First visit",
    "visit.returning": "Returning",
    "visit.noun": "visit",
    "visit.recognized": "Recognized visitor",
    "visit.visitor": "Visitor",
    "visit.technical": "Technical hosting/VPN request",
    "visit.deviceMissing": "Device not collected",
    "visit.select": "select",
    "consent.accepted": "Accepted",
    "consent.notGranted": "No consent",
    "consent.withdrawn": "Withdrawn",
    "consent.expired": "Expired",
    "consent.unknown": "Not collected",
    "map.label": "Activity map",
    "map.precise": "map points",
    "map.loading": "Loading map data …",
    "map.recognized": "Recognized company",
    "map.noConsent": "No consent",
    "map.accepted": "Accepted",
    "map.live": "Live",
    "map.countryActivity": "Country level (no precise place)",
    "map.visitors": "Visitors",
    "map.recognizedVisitors": "Recognized companies",
    "map.bots": "Bots & technical",
    "map.recentered": "Fitted to visible activities",
    "map.2dActive": "2D view active",
    "map.3dActive": "3D view active"
  }
} as const;

export type MessageKey = keyof typeof messages.de;

export function setLocale(next: AppLocale) {
  locale.value = next;
  document.documentElement.lang = next;
  try { localStorage.setItem(STORAGE_KEY, next); } catch { /* no-op */ }
}

export function t(key: MessageKey, variables: Record<string, string | number> = {}) {
  let value: string = messages[locale.value][key] ?? messages.de[key] ?? key;
  Object.entries(variables).forEach(([name, replacement]) => {
    value = value.replaceAll(`{${name}}`, String(replacement));
  });
  return value;
}

export function useI18n() {
  return { locale: computed(() => locale.value), setLocale, t };
}

export function localeTag() {
  return locale.value === "de" ? "de-AT" : "en-GB";
}

export function formatDateTime(value: string | Date, options: Intl.DateTimeFormatOptions = { dateStyle: "short", timeStyle: "medium" }) {
  return new Intl.DateTimeFormat(localeTag(), options).format(new Date(value));
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(localeTag(), options).format(value);
}

export function regionName(countryCode: string, fallback = countryCode) {
  if (!/^[A-Z]{2}$/.test(countryCode)) return fallback;
  return new Intl.DisplayNames([locale.value === "de" ? "de" : "en"], { type: "region" }).of(countryCode) || fallback;
}

const englishDomainLabels: Record<string, string> = {
  "Direkt": "Direct",
  "Interne Navigation": "Internal navigation",
  "Organic Search": "Organic search",
  "Paid Search": "Paid search",
  "E-Mail": "Email",
  "Paid Campaign": "Paid campaign",
  "Ohne Zuordnung": "Unattributed",
  "Startseite": "Home",
  "Preise": "Pricing",
  "Kontakt": "Contact",
  "Referenzen": "Case studies",
  "Lösung": "Solution",
  "Lösungen": "Solutions",
  "Produkt": "Product",
  "Integrationen": "Integrations",
  "Ressourcen": "Resources",
  "Unternehmen": "Company",
  "Rechtliches": "Legal",
  "Sicherheit": "Security",
  "Nicht erfasst": "Not collected",
  "Österreich": "Austria",
  "Deutschland": "Germany",
  "Schweiz": "Switzerland"
};

/** Localizes controlled labels and display strings without changing stored/API data. */
export function localizeDomainText(value: string) {
  if (locale.value === "de" || !value) return value;
  if (englishDomainLabels[value]) return englishDomainLabels[value];
  let result = value;
  Object.entries(englishDomainLabels)
    .sort(([left], [right]) => right.length - left.length)
    .forEach(([source, target]) => { result = result.replaceAll(source, target); });
  return result
    .replace(/(\d+)\s*Sek\./g, "$1 sec")
    .replace(/(\d+)\s*Min\./g, "$1 min")
    .replace(/(\d+)\s*Std\./g, "$1 hr");
}

setLocale(initialLocale);
